import { Button, Flex, notify, Tooltip, Typography } from 'front-commons/ds';
import { Typography as TypografyDS, Icon } from 'hype-ds';
import { useDebounce, useWhenMounted, useDynamicLoading } from 'front-commons/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddToBasketButton from 'containers/AddToBasketButton';
import usePromotionContext from 'contexts/Promotion';
import { updateOrderImportPromotion } from 'services/import';
import useDialog from 'stores/dialog';
import useDrawer from 'stores/drawer';
import useOrderImport from 'stores/orderImport';
import { PromotionsProps } from 'stores/orderImport/interfaces';
import {
	ImportPromotionsActionButtonsAddButtonProps,
	ImportPromotionsActionButtonsProps,
	LoadingType,
} from './interfaces';
import useCustomer from 'stores/customer';
import { FlexTooltip } from './styles';
import { manageBasketProductAmount, replaceProducts } from 'services/basket';
import useBasket from 'stores/basket';

export default function ImportPromotionsActionButtons({
	posId,
	isFlex = false,
	hasValue,
	promotionId,
	replaceSource,
	comboProducts,
	distributorId,
	availableLimit,
	sourceProductId,
	productsSummary,
	suggestionGroupId,
	suggestionGroupName,
	hasValidDistributors,
	orderImportProductId,
	getOrderImportProductId,
	minQuantityDistinctProducts,
	progressiveDiscountProductMix,

	handleSetDialogState,
	handleRefetchItemList,
	handleQuantityImportChange,
	onAfterReplaceSourceProduct,
}: ImportPromotionsActionButtonsProps) {
	const navigate = useNavigate();

	const { handleLoading } = useDynamicLoading<LoadingType>();
	const {
		orderImportStore: { posPromotions },
		handleAddPosPromotions,
		handleRemovePosSinglePromotion,
	} = useOrderImport();
	const { handleCloseAllDrawers, handleCloseDrawer } = useDrawer();
	const { closePromotionDrawer } = usePromotionContext();
	const { handleOpenDialog } = useDialog();
	const { handleHasPermission } = useCustomer();
	const { handleRemoveFromBasket } = useBasket();

	const importData = posPromotions?.[posId]?.[promotionId];
	const hasInList = !!importData;
	const isInvalid = false;
	const validProductsCount = productsSummary.filter((product: any) => product.quantity > 0).length;
	const isValidQuantity = validProductsCount >= minQuantityDistinctProducts!;

	const prevProductsSummaryRef = useRef<any[]>([]);
	const hasPermission = handleHasPermission('BASKET_EDIT');

	useEffect(() => {
		prevProductsSummaryRef.current = productsSummary;
	}, [productsSummary]);

	const checkQuantityChange = (): boolean => {
		return productsSummary.some((product: any, index: number) => {
			const prevProduct = prevProductsSummaryRef.current[index];
			return prevProduct && product.quantity !== prevProduct.quantity;
		});
	};

	const handleOpenConflictModal = () => {
		handleOpenDialog({
			heading: {
				title: 'Promoção não pode ser adicionada',
				showCloseButton: true,
			},
			content: {
				description: (
					<TypografyDS.Paragraph.M whiteSpace="pre-line">
						Um dos itens desta promoção já está incluído em outra promoção do seu pedido. {'\n'} Um mesmo produto não
						pode participar de duas promoções ao mesmo tempo. {'\n'} Acesse o pedido para verificar e ajustar.
					</TypografyDS.Paragraph.M>
				),
			},
			footer: {
				primaryButton: { label: 'Entendi' },
			},
		});
	};

	const handleAdd = async () => {
		if (!hasValue) {
			handleSetDialogState('no_value');
			return;
		}

		try {
			handleLoading().set(['quantity']);

			if (!replaceSource) {
				await updateOrderImportPromotion({
					orderImportProductId,
					distributorId,
					quantity: 1,
					removed: false,
					comboId: promotionId,
					comboProducts,
				});
			}

			if (replaceSource && sourceProductId) {
				await replaceProducts({
					posId,
					products: [
						{
							addProduct: {
								distributorId,
								productId: promotionId,
								quantity: 1,
								productsInCombo: productsSummary.map(({ productId, quantity }: any) => ({
									productId,
									quantity,
								})),
							},
							removeProductId: sourceProductId,
						},
					],
				});

				onAfterReplaceSourceProduct?.();
			}

			handleAddPosPromotions({
				posId,
				promotionId,
				payload: {
					products: comboProducts,
					orderImportProductId,
					quantity: 1,
					distributorId,
				},
			});

			if (!replaceSource) handleCloseAllDrawers();
			if (replaceSource) handleCloseDrawer('import-promotion-details-drawer');
			closePromotionDrawer();
			handleRefetchItemList?.();
			notify.positive({ description: 'Promoção adicionada com sucesso.' });
		} catch (error) {
			const err = error as any;

			if (err.status === 'not_acceptable') {
				notify.negative({ description: 'Importação cancelada.' });
				navigate('/importacao-pedido');
				return;
			}

			if (err.status === 'bad_request') {
				handleOpenConflictModal();
				return;
			}
			notify.negative({ description: 'Não foi possível adicionar essa promoção! Por favor, tente novamente.' });
		} finally {
			handleLoading().remove(['quantity']);
		}
	};

	const handleUpdate = async (payload: Partial<PromotionsProps & { removed: boolean }>) => {
		const loadingState = payload.removed ? 'remove' : 'quantity';

		try {
			handleLoading().set([loadingState]);
			const promotionInfos = payload.removed ? undefined : { comboProducts };

			if (replaceSource) {
				if (!payload.removed) {
					const requestData = {
						quantity: isFlex ? payload.quantity || 1 : 1,
						distributorId,
						productId: promotionId,
						suggestionGroupId,
						suggestionGroupName,
						productsInCombo: productsSummary.map(({ productId, quantity }: any) => ({
							quantity,
							productId,
						})),
					};

					await manageBasketProductAmount({ posId, data: requestData });
				}

				if (payload.removed) {
					await handleRemoveFromBasket(promotionId);
				}
			}

			if (!replaceSource) {
				await updateOrderImportPromotion({
					orderImportProductId: getOrderImportProductId?.() || orderImportProductId,
					distributorId,
					quantity: 1,
					removed: !hasValue,
					...promotionInfos,
					...payload,
				});
			}

			if (payload.removed || !hasValue || payload.quantity === 0) {
				handleRemovePosSinglePromotion({ posId, promotionId });
				handleRefetchItemList?.();
				notify.positive({ description: 'Promoção removida com sucesso.' });
				handleQuantityImportChange?.(0);
				if (!replaceSource) handleCloseAllDrawers();
				closePromotionDrawer();
				return;
			}

			handleAddPosPromotions({
				posId,
				promotionId,
				payload: {
					products: comboProducts,
					quantity: 1,
					distributorId,
					orderImportProductId: getOrderImportProductId?.() || orderImportProductId,
					...payload,
				},
			});

			handleRefetchItemList?.();
			handleQuantityImportChange?.(payload.quantity || 1);
			notify.positive({ description: 'Promoção editada com sucesso.' });
		} catch (error) {
			const errorResponse = error as any;

			if (errorResponse.status === 'not_acceptable') {
				notify.negative({ description: 'Importação cancelada.' });
				navigate('/importacao-pedido');
				return;
			}

			if (errorResponse.code === 400 && errorResponse.status === 'bad_request') {
				handleOpenConflictModal();
				return;
			}

			notify.negative({ description: 'Falha ao atualizar promoção' });
		} finally {
			handleLoading().remove([loadingState]);
		}
	};

	const addToCartLabel = useMemo(() => {
		if (minQuantityDistinctProducts) {
			if (isValidQuantity) return 'Adicionar promoção';
			return 'Promoção bloqueada';
		}

		if (checkQuantityChange()) {
			if (!isValidQuantity) return 'Promoção bloqueada';
			return 'Atualizar';
		}

		return 'Adicionar';
	}, [minQuantityDistinctProducts, prevProductsSummaryRef, comboProducts.length, isValidQuantity]);

	const renderFlexBtn = () => {
		return (
			<Button
				width={{
					small: '100%',
					medium: ['Promoção bloqueada'].includes(addToCartLabel) ? '236px' : '167.65px',
				}}
				onClick={handleAdd}
				loading={handleLoading().get(['quantity'])}
				leftIcon={
					addToCartLabel === 'Promoção bloqueada'
						? {
								name: 'lock',
								color: '--text-invert',
								detached: !!minQuantityDistinctProducts,
						  }
						: undefined
				}
				hasPermission={hasPermission}
				disabled={(!isValidQuantity && !checkQuantityChange()) || ['Promoção bloqueada'].includes(addToCartLabel)}
				data-component-name={addToCartLabel === 'Adicionar promoção' ? 'add_to_cart' : undefined}
			>
				{addToCartLabel}
			</Button>
		);
	};

	const renderprogressiveDiscountBtn = () => {
		return (
			<Flex direction="column" width="100%" justifyContent="space-between" alignItems="center" gap="8px">
				<Flex gap="4px" alignItems="center" justifyContent={hasInList ? 'flex-end' : 'initial'} alignSelf="end">
					{!hasInList ? (
						<Typography variant="ParagraphSmall/Regular" color="--text-secondary">
							{validProductsCount} /{' '}
							<Typography variant="ParagraphSmall/Semibold" color="--text-primary">
								{minQuantityDistinctProducts}
							</Typography>{' '}
							Produtos para desbloquear
						</Typography>
					) : (
						<>
							<TypografyDS.Label.XS color="--text-primary">
								{validProductsCount} / {minQuantityDistinctProducts}
							</TypografyDS.Label.XS>
							<TypografyDS.Paragraph.XS color="--text-primary">Produtos para desbloquear</TypografyDS.Paragraph.XS>
						</>
					)}

					<FlexTooltip>
						<Tooltip
							title="Promoção bloqueada"
							content="É necessário a escolha de produtos distintos para desbloquear a promoção."
							maxWidth="174px"
							onDialog={handleOpenDialog}
						>
							<Icon name="info" color="--text-primary" size="16px" fill />
						</Tooltip>
					</FlexTooltip>
				</Flex>

				<Flex
					width="100%"
					justifyContent="end"
					gap="8px"
					alignItems="center"
					direction={{ small: 'column', medium: 'row' }}
				>
					{hasInList && hasPermission && (
						<Button
							loading={handleLoading().get(['remove'])}
							disabled={handleLoading().get(['quantity'])}
							variant="secondary"
							onClick={() => handleUpdate({ removed: true })}
							width="100%"
							maxWidth={{ medium: '160px' }}
							height="40px"
						>
							Remover
						</Button>
					)}
					{hasInList && hasPermission && isValidQuantity && isValidQuantity !== undefined ? (
						<ImportPromotionsActionButtons.ManageButtons
							isFlex={isFlex}
							loading={handleLoading().get(['quantity'])}
							disabled={handleLoading().get(['quantity', 'remove']) || isInvalid}
							importData={importData}
							comboProducts={comboProducts}
							availableLimit={availableLimit}
							onQuantityChange={(quantity) => handleUpdate({ quantity })}
							suggestionGroupId={suggestionGroupId}
							suggestionGroupName={suggestionGroupName}
							isValidQuantity={isValidQuantity}
						/>
					) : (
						renderFlexBtn()
					)}
				</Flex>
			</Flex>
		);
	};

	const renderDefaultComponent = () => {
		return (
			<Flex justifyContent="flex-end" margin="0 0 32px 0" gap="16px" width="100%">
				{hasInList && !replaceSource && (
					<Button
						loading={handleLoading().get(['remove'])}
						disabled={handleLoading().get(['quantity'])}
						variant="secondary"
						onClick={() => handleUpdate({ removed: true })}
						width={{ medium: '100%' }}
						maxWidth={{ medium: '165.61px' }}
						height="40px"
					>
						Remover promoção
					</Button>
				)}
				{hasInList ? (
					hasPermission && (
						<ImportPromotionsActionButtons.ManageButtons
							isFlex={isFlex}
							loading={handleLoading().get(['quantity'])}
							disabled={handleLoading().get(['quantity', 'remove']) || isInvalid}
							importData={importData}
							comboProducts={comboProducts}
							availableLimit={availableLimit}
							onQuantityChange={(quantity) => handleUpdate({ quantity })}
							suggestionGroupId={suggestionGroupId}
							suggestionGroupName={suggestionGroupName}
						/>
					)
				) : (
					<Button
						disabled={!hasValidDistributors || handleLoading().get(['remove'])}
						loading={handleLoading().get(['quantity'])}
						onClick={handleAdd}
						width="100%"
						size="large"
						maxWidth={{ medium: '167.75px' }}
						data-component-name="add_to_cart"
					>
						Adicionar promoção
					</Button>
				)}
			</Flex>
		);
	};

	return progressiveDiscountProductMix && minQuantityDistinctProducts
		? renderprogressiveDiscountBtn()
		: renderDefaultComponent();
}

ImportPromotionsActionButtons.ManageButtons = ({
	isFlex,
	loading,
	disabled,
	importData,
	comboProducts,
	availableLimit,
	suggestionGroupId,
	suggestionGroupName,
	isValidQuantity,
	onQuantityChange,
}: ImportPromotionsActionButtonsAddButtonProps) => {
	const { summary } = usePromotionContext();
	const [quantity, setQuantity] = useState(importData.quantity || 0);

	const someQuantityHasChanged = useMemo(() => {
		return (comboProducts || summary).some((productSummary) => {
			const importedProduct = importData.products.find((imported) => imported.productId === productSummary.productId);
			if (!importedProduct) return productSummary.quantity !== 0;

			return importedProduct.quantity !== productSummary.quantity;
		});
	}, [importData, summary, comboProducts]);

	const debouncedQuantity = useDebounce(quantity, 500);

	useWhenMounted(() => {
		onQuantityChange(debouncedQuantity);
	}, [debouncedQuantity]);

	if (!isFlex)
		return (
			<Button
				width={{ small: '100%', medium: '167.75px' }}
				disabled={!someQuantityHasChanged || disabled || (!isValidQuantity && isValidQuantity !== undefined)}
				loading={loading}
				onClick={() => onQuantityChange(quantity)}
				height="40px"
			>
				Atualizar promoção
			</Button>
		);

	return (
		<AddToBasketButton
			hasPermission
			initialQuantity={quantity}
			outOfContext
			allowZero
			allowEmpty
			onItemChange={setQuantity}
			counterOptions={{
				hasPermission: true,
				loading: loading ? 'typing' : undefined,
				disabled: disabled || availableLimit === 0,
				width: '167.75px',
			}}
			maxQuantity={availableLimit}
			suggestionGroupId={suggestionGroupId}
			suggestionGroupName={suggestionGroupName}
		/>
	);
};
